import { __awaiter } from "tslib";
import * as Api from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import * as Logging from "../Logging/Logging";
import { DynamicContentTransport } from "../DynamicContent/DynamicContentTransport";
import { isDate, isNOU, isNumber, isValidUserId, stringToDate } from "@ms-ofb/officefloodgatecore/dist/src/Utils";
import { GovernanceServiceClientState, GovernanceServiceSurveyType } from "@ms-ofb/officefloodgatecore/dist/src/Api/IGovernanceServiceClient";
import { GovernedChannelType } from "@ms-ofb/officefloodgatecore/dist/src/GovernedChannel";
const govServiceInt = "https://onessdev.eastus2.cloudapp.azure.com/api/v1.0/UserSurvey";
const govServiceProd = "https://oness.microsoft.com/api/v1.0/UserSurvey";
export class GovernanceServiceClient {
  constructor() {
    this.dynamicTransport = new DynamicContentTransport();
    this.governanceServiceEnabled = false;
    this.forceServicelessSurveyDisplay = false;
    this.lastSurveyFailedSync = undefined;
    this.clientState = GovernanceServiceClientState.Disabled;
  }
  setup(configuration) {
    this.governanceServiceEnabled = configuration.getFloodgateInitOptions().governanceServiceEnabled;
    if (this.governanceServiceEnabled) {
      // setup config and client info
      this.clientInfo = {
        applicationId: configuration.getCommonInitOptions().appId.toString(),
        sessionId: configuration.getCommonInitOptions().sessionId,
        version: configuration.getCommonInitOptions().build,
        surveyPlatform: 1 /* Floodgate */,
        hostPlatform: 6 /* Web */,
        tenantId: configuration.getCommonInitOptions().telemetryGroup.tenantId,
        deviceId: configuration.getCommonInitOptions().telemetryGroup.deviceId,
        audience: configuration.getCommonInitOptions().telemetryGroup.audience
      };
      this.userId = configuration.getCommonInitOptions().telemetryGroup.loggableUserId;
      // Ensure all the client info is needed
      if (isNOU(this.clientInfo.sessionId)) {
        this.clientState = GovernanceServiceClientState.InitializationFailed;
        return;
      }
      if (isNOU(this.clientInfo.version)) {
        this.clientState = GovernanceServiceClientState.InitializationFailed;
        return;
      }
      if (!isValidUserId(this.userId)) {
        this.clientState = GovernanceServiceClientState.InitializationFailed;
        return;
      }
      // Setup governance service config parameters
      this.forceServicelessSurveyDisplay = configuration.getFloodgateInitOptions().governanceServiceConfig.forceServicelessSurveyDisplay;
      this.retryLimit = configuration.getFloodgateInitOptions().governanceServiceConfig.retry;
      this.timeout = configuration.getFloodgateInitOptions().governanceServiceConfig.timeout;
      this.isProduction = configuration.getCommonInitOptions().environment === 0 /* Production */;
      this.clientState = GovernanceServiceClientState.Enabled;
    }
  }
  isEnabled(governedChannelType) {
    return this.clientState === GovernanceServiceClientState.Enabled && this.governanceServiceEnabled && governedChannelType === GovernedChannelType.Standard;
  }
  shouldForceDisplay() {
    return this.forceServicelessSurveyDisplay;
  }
  getClientInfo() {
    return this.clientInfo;
  }
  getClientState() {
    return this.clientState;
  }
  getLastSurveyFailedSync() {
    return this.lastSurveyFailedSync;
  }
  checkGovernancePermitAsync(survey, surveyLevelCooldown, userLevelCooldown, channel) {
    return __awaiter(this, void 0, void 0, function* () {
      const requestUrl = this.isProduction ? govServiceProd : govServiceInt;
      const surveyInfo = this.buildSurveyInfo(survey, surveyLevelCooldown, channel);
      const body = this.buildBodyRequest(surveyInfo, userLevelCooldown);
      const serviceResult = {
        permission: false,
        error: false
      };
      let response;
      try {
        const timeBefore = performance.now();
        response = yield this.dynamicTransport.postRequestWithRetry(requestUrl, body, this.retryLimit, this.timeout);
        // Log the success criteria with time
        const timeAfter = performance.now();
        Logging.getLogger().logEvent(Logging.EventIds.GOVERNANCE_SERVICERESULT, 1 /* Critical */, {
          TimeMilliseconds: timeAfter - timeBefore,
          HttpStatusCode: response.status,
          HttpStatusText: response.statusText,
          AdditionalSurveyInfo: this.buildConfigTelemetryString(),
          CorrelationId: this.dynamicTransport.requestId
        });
        if (response.ok) {
          // Get the response text
          const responseText = yield response.text();
          // Parse the response
          const jsonResponse = JSON.parse(responseText);
          // Reparsing the date to correct ISO format
          jsonResponse.lastTriggerSurveyTime = stringToDate(new Date(jsonResponse.lastTriggerSurveyTime).toISOString());
          // Check the response integrity
          if (!isNumber(jsonResponse.timeUntilNextSurveyAllowed) || !isDate(jsonResponse.lastTriggerSurveyTime)) {
            // Throw error with the response
            serviceResult.errorMessage = "Service Response is not formatted correctly: " + responseText;
            serviceResult.error = true;
            this.lastSurveyFailedSync = surveyInfo;
          } else {
            // Check if this is permitted
            if (jsonResponse.timeUntilNextSurveyAllowed === 0) {
              serviceResult.permission = true;
            }
            // Save the response trigger time
            serviceResult.lastTriggerSurvey = jsonResponse;
            // Clear the last failure
            this.lastSurveyFailedSync = undefined;
          }
        } else {
          this.lastSurveyFailedSync = surveyInfo;
          serviceResult.errorMessage = `Server Error, status: ${response.status}, status text: ${response.statusText}`;
          serviceResult.error = true;
        }
      } catch (e) {
        this.lastSurveyFailedSync = surveyInfo;
        Logging.getLogger().logEvent(Logging.EventIds.GOVERNANCE_SERVICEFAILURE, 2 /* Error */, {
          ErrorMessage: e.toString(),
          AdditionalSurveyInfo: this.buildConfigTelemetryString(),
          CorrelationId: this.dynamicTransport.requestId,
          HttpStatusCode: response === null || response === void 0 ? void 0 : response.status,
          HttpStatusText: response === null || response === void 0 ? void 0 : response.statusText
        });
        serviceResult.error = true;
        serviceResult.errorMessage = `Error: ${e.toString()}, Error stack: ${e.stack}`;
      }
      return serviceResult;
    });
  }
  buildSurveyInfo(survey, surveyLevelCooldown, channel) {
    const stringType = Api.ISurvey.Type[survey.getType()];
    const surveyType = GovernanceServiceSurveyType[stringType];
    return {
      surveyId: survey.getCampaignId(),
      surveyTriggerTime: new Date(),
      surveyType: surveyType,
      surveyLevelCooldown: surveyLevelCooldown,
      surveyChannel: channel
    };
  }
  buildBodyRequest(surveyInfo, userLevelCooldown) {
    const result = {
      userId: this.userId,
      userLevelCooldown: userLevelCooldown,
      survey: surveyInfo,
      client: this.clientInfo
    };
    return JSON.stringify(result);
  }
  buildConfigTelemetryString() {
    const objectTelemetry = {
      retry: this.retryLimit,
      timeout: this.timeout
    };
    return JSON.stringify(objectTelemetry);
  }
}
const governanceServiceClient = new GovernanceServiceClient();
/**
 * Get the current Governance Service Client
 */
export function get() {
  return governanceServiceClient;
}